import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { AtomicLoadingScreen } from '@/components/ui/atomic-loader';
import { 
  Settings,
  MessageSquare,
  FileText,
  Link2,
  Target,
  Tag,
  Ban,
  Send
} from 'lucide-react';
import { Client } from '@/types/client';
import BusinessRulesTabNavigation from './business-rules/BusinessRulesTabNavigation';
import IntegrationTab from './business-rules/IntegrationTab';
import ApiTabClean from './business-rules/ApiTabClean';
import BillingTab from './business-rules/BillingTab';
import { MessagesTab } from './business-rules/MessagesTab';
import { MetaApiTemplatesTab } from './business-rules/MetaApiTemplatesTab';
import TemplateStrategiesTab from './business-rules/TemplateStrategiesTab';
import MessageFlowsTab from './business-rules/MessageFlowsTab';
import TagsTab from './business-rules/TagsTab';
import BlockedContactsTab from './business-rules/BlockedContactsTab';
import EmailTemplatesManager from './email/EmailTemplatesManager';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { apiService } from '@/lib/api'; // 1. Importar o nosso serviço de API

interface BusinessRulesModalProps {
  client: Client | null;
  isOpen: boolean;
  onClose: () => void;
}

const BusinessRulesModal: React.FC<BusinessRulesModalProps> = ({
  client,
  isOpen,
  onClose
}) => {
  console.log('🏢 [BusinessRulesModal] Modal aberto para cliente:', {
    id: client?.id,
    legal_name: client?.legal_name,
    trade_name: client?.trade_name,
    isOpen
  });
  
  const [activeTab, setActiveTab] = useState('integration');
  const [messagesTipo, setMessagesTipo] = useState<'meta' | 'email'>('meta');
  const [hasExtraOficialConnection, setHasExtraOficialConnection] = useState(false);
  const [loadingConnections, setLoadingConnections] = useState(true);

  // Verificar se existe conexão extra_oficial
  useEffect(() => {
    const checkExtraOficialConnections = async () => {
      if (!client?.id) return;
      
      setLoadingConnections(true);
      try {
        const response = await apiService.get(`/whatsapp/client/${client.id}`);
        console.log('🔍 [BusinessRulesModal] Response completo:', response);
        
        // Tratar diferentes formatos de resposta
        let connections = [];
        if (Array.isArray(response)) {
          connections = response;
        } else if (response.data && Array.isArray(response.data)) {
          connections = response.data;
        } else if (response.connections && Array.isArray(response.connections)) {
          connections = response.connections;
        }
        
        console.log('🔍 [BusinessRulesModal] Conexões encontradas:', connections);
        
        const hasExtraOficial = connections.some((conn: any) => {
          console.log('🔍 [BusinessRulesModal] Verificando conexão:', {
            id: conn.id,
            name: conn.name,
            type: conn.connection_type
          });
          return conn.connection_type === 'extra_oficial';
        });
        
        setHasExtraOficialConnection(hasExtraOficial);
        console.log('✅ [BusinessRulesModal] Tem conexão Extra Oficial?', hasExtraOficial);
      } catch (error) {
        console.error('❌ [BusinessRulesModal] Erro ao verificar conexões:', error);
        setHasExtraOficialConnection(false);
      } finally {
        setLoadingConnections(false);
      }
    };

    if (isOpen && client) {
      checkExtraOficialConnections();
    }
  }, [client, isOpen]);

  // Construir lista de abas dinamicamente
  const baseTabs = [
    { value: 'integration', label: 'Integração', icon: Link2 },
    { value: 'api', label: 'API de envio', icon: Settings },
    { value: 'messages', label: 'Mensagens', icon: MessageSquare },
    { value: 'tags', label: 'Tags', icon: Tag },
    { value: 'template-strategies', label: 'Estratégias', icon: Target },
  ];

  // Adicionar aba de Fluxos de Envio apenas se houver conexão extra_oficial
  const tabs = hasExtraOficialConnection
    ? [
        ...baseTabs,
        { value: 'message-flows', label: 'Fluxos de Envio', icon: Send },
        { value: 'billing', label: 'Automação', icon: FileText },
        { value: 'blocked-contacts', label: 'Contatos Bloqueados', icon: Ban },
      ]
    : [
        ...baseTabs,
        { value: 'billing', label: 'Automação', icon: FileText },
        { value: 'blocked-contacts', label: 'Contatos Bloqueados', icon: Ban },
      ];

  if (!client) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl h-[90vh] flex flex-col bg-white shadow-xl border border-gray-200">
        <DialogHeader className="border-b border-gray-100 pb-4 bg-white">
          <DialogTitle className="text-2xl font-montserrat font-semibold text-gray-900">
            Regra de negócio - {client.legal_name}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 flex flex-col overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full flex flex-col h-full">
            {/* Navigation tabs at the top */}
            <div className="flex-shrink-0 pt-4">
              <BusinessRulesTabNavigation activeTab={activeTab} tabs={tabs} />
            </div>

            {/* Content area below */}
            <div className="flex-1 overflow-y-auto mt-6">
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-8 mx-2">
                {activeTab === 'integration' && (
                  <IntegrationTab client={client} />
                )}
                {activeTab === 'api' && <ApiTabClean client={client} />}
                {activeTab === 'billing' && <BillingTab client={client} />}
                {activeTab === 'messages' && (
                  <div className="space-y-6">
                    <div className="flex items-center gap-3">
                      <Label className="text-sm whitespace-nowrap">Tipo de template:</Label>
                      <Select value={messagesTipo} onValueChange={(v) => setMessagesTipo(v as 'meta' | 'email')}>
                        <SelectTrigger className="w-[260px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="meta">WhatsApp (Meta / ÁtomosChat)</SelectItem>
                          <SelectItem value="email">Email</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {messagesTipo === 'meta' ? (
                      <MetaApiTemplatesTab clientId={client.id} />
                    ) : (
                      <EmailTemplatesManager clientId={client.id} />
                    )}
                  </div>
                )}
                {activeTab === 'template-strategies' && <TemplateStrategiesTab client={client} />}
                {activeTab === 'message-flows' && hasExtraOficialConnection && <MessageFlowsTab client={client} />}
                {activeTab === 'tags' && <TagsTab client={client} />}
                {activeTab === 'blocked-contacts' && <BlockedContactsTab client={client} />}
              </div>
            </div>
          </Tabs>
        </div>

      </DialogContent>
    </Dialog>
  );
};

export default BusinessRulesModal;
